import React, { createContext, useContext, ReactNode } from 'react';
import { createStoresWithStorage } from '../stores/childNavStores';
import { StorageInterface } from '../stores/childNavStores';
import { convertAppProfileToChildProfile } from '../types/profile';
import { AppProfileStorage } from '../services/AppProfileStorage';

// Define the store context type
type StoresType = ReturnType<typeof createStoresWithStorage>;

// Cache the React context on globalThis so that Vite HMR module re-evaluations
// reuse the same context object rather than creating a new one. Without this,
// after any hot reload the StoreProvider hands a value to the OLD context while
// child components read from the NEW context → "useStores must be used within a
// StoreProvider" error at runtime.
const _g = globalThis as any;
if (!_g.__childNavStoreContext) {
  _g.__childNavStoreContext = createContext<StoresType | null>(null);
}
const StoreContext: React.Context<StoresType | null> = _g.__childNavStoreContext;

interface StoreProviderProps {
  children: ReactNode;
  storage: StorageInterface;
  apiService?: any; // ApiService type
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ 
  children, 
  storage, 
  apiService 
}) => {
  // Create stores with the provided storage
  const stores = React.useMemo(() => {
    const storeInstances = createStoresWithStorage(storage);
    
    // Set API service if provided
    if (apiService && storeInstances.setApiService) {
      storeInstances.setApiService(apiService);
    }
    
    return storeInstances;
  }, [storage, apiService]);

  // Initialize app on mount
  React.useEffect(() => {
    console.log('🔧 StoreProvider: Effect triggered, stores available:', !!stores);
    
    if (stores && stores.initializeApp) {
      console.log('🔧 StoreProvider: Calling initializeApp...');
      
      // Process pending profile syncs FIRST, before regular app initialization
      const processPendingProfiles = async () => {
        if (!storage || !apiService) {
          console.log('🔧 StoreProvider: Skipping pending profile check - missing dependencies');
          return;
        }
        
        try {
          console.log('🔍 StoreProvider: Checking for pending profile sync...');
          const hasPendingSync = await storage.getItem('profiles_pending_sync');
          
          if (hasPendingSync === 'true') {
            console.log('✅ StoreProvider: Found pending profile sync - processing immediately');
            
            const pendingSyncData = await storage.getItem('pending_profile_sync');
            if (pendingSyncData) {
              const syncData = JSON.parse(pendingSyncData);
              const { authData, serverProfiles, accountId } = syncData;
              
              console.log('🔄 StoreProvider: Processing pending profiles:', serverProfiles.length);
              
              // Get store instances
              const profileStore = stores.useProfileStore;
              const appProfileStorage = new AppProfileStorage(storage);
              
              // Download and save each profile
              for (const serverProfile of serverProfiles) {
                try {
                  console.log(`📥 StoreProvider: Downloading queued profile ${serverProfile.profileId}`);
                  
                  const serviceRequest = {
                    RequestType: 'profile_get',
                    Payload: JSON.stringify({
                      profileId: serverProfile.profileId,
                      accountId: accountId
                    })
                  };
                  
                  const profileResponse = await apiService.processServiceRequestWithRefresh(serviceRequest, 'sync', {
                    userStore: (global as any).__userStore,
                    storage,
                  });
                  
                  if (profileResponse && profileResponse.success && profileResponse.payload) {
                    console.log(`✅ StoreProvider: Profile ${serverProfile.profileId} downloaded successfully`);
                    
                    const appProfileDto = JSON.parse(profileResponse.payload);
                    console.log('🔎 StoreProvider profile_get payload track counts:', {
                      profileId: appProfileDto.profileId ?? appProfileDto.ProfileId,
                      prescriptionsCamel: appProfileDto.prescriptions?.events?.length ?? 0,
                      prescriptionsPascal: appProfileDto.Prescriptions?.Events?.length ?? 0,
                      referralsCamel: appProfileDto.referrals?.events?.length ?? 0,
                      referralsPascal: appProfileDto.Referrals?.Events?.length ?? 0,
                    });
                    
                    // Save to AppProfileStorage
                    await appProfileStorage.saveProfile(appProfileDto);
                    console.log(`💾 StoreProvider: Profile ${serverProfile.profileId} saved to AppProfileStorage`);
                    
                    // Convert and add to profile store directly
                    const childProfile = convertAppProfileToChildProfile(appProfileDto, storage);
                    
                    if (childProfile) {
                      // Skip profiles that were deleted on the server
                      if (childProfile.profileStatus === -1) {
                        console.log(`🗑️ StoreProvider: Skipping deleted profile ${serverProfile.profileId}`);
                        continue;
                      }
                      
                      // Add profile directly to the store
                      const currentState = profileStore.getState();
                      const existingProfileIndex = currentState.profiles.findIndex((p: any) => p.id === childProfile.id);
                      
                      let updatedProfiles;
                      if (existingProfileIndex >= 0) {
                        // Update existing profile
                        updatedProfiles = [...currentState.profiles];
                        updatedProfiles[existingProfileIndex] = childProfile;
                        console.log(`🔄 StoreProvider: Updated existing profile ${childProfile.id} in store`);
                      } else {
                        // Add new profile
                        updatedProfiles = [...currentState.profiles, childProfile];
                        console.log(`➕ StoreProvider: Added new profile ${childProfile.id} to store`);
                      }
                      
                      // Update the store state
                      profileStore.setState({
                        profiles: updatedProfiles,
                        activeProfileId: currentState.activeProfileId || childProfile.id,
                        developmentAges: currentState.developmentAges,
                        appProfileStorage: currentState.appProfileStorage
                      });
                      
                      // Also update the persistence layer manually
                      const persistenceData = {
                        profiles: updatedProfiles,
                        activeProfileId: currentState.activeProfileId || childProfile.id,
                        developmentAges: currentState.developmentAges
                      };
                      await storage.setItem('child-nav-profiles', JSON.stringify(persistenceData));
                      console.log(`💾 StoreProvider: Updated profile store persistence for ${childProfile.id}`);
                    }
                  } else {
                    console.error(`❌ StoreProvider: Failed to download profile ${serverProfile.profileId}:`, profileResponse?.message);
                  }
                } catch (profileError) {
                  console.error(`❌ StoreProvider: Error processing profile ${serverProfile.profileId}:`, profileError);
                }
              }
              
              // Get final profile count
              const finalState = profileStore.getState();
              console.log(`✅ StoreProvider: Profile store now has ${finalState.profiles.length} profiles after sync`);
              
              console.log(`✅ StoreProvider: Pending profile sync completed - processed ${serverProfiles.length} profiles`);
            }
          } else {
            console.log('ℹ️ StoreProvider: No pending profile sync needed');
          }
          
          // Clear pending sync data after processing
          await storage.removeItem('pending_profile_sync');
          await storage.removeItem('profiles_pending_sync');
        } catch (error) {
          console.error('❌ StoreProvider: Failed to process pending profile sync:', error);
          // Clean up on error
          try {
            await storage.removeItem('pending_profile_sync');
            await storage.removeItem('profiles_pending_sync');
          } catch (cleanupError) {
            console.error('❌ StoreProvider: Failed to clean up after error:', cleanupError);
          }
        }
      };
      
      // Execute pending profile processing BEFORE regular initialization
      processPendingProfiles().then(() => {
        // Now proceed with regular app initialization
        return stores.initializeApp();
      }).then(() => {
        console.log('🔧 StoreProvider: initializeApp completed successfully');
      }).catch(error => {
        console.error('StoreProvider: Failed to initialize app:', error);
      });
    } else {
      console.log('🔧 StoreProvider: No stores or initializeApp method available');
    }
  }, [stores, storage, apiService]);

  return (
    <StoreContext.Provider value={stores}>
      {children}
    </StoreContext.Provider>
  );
};

// Hooks to use the stores
export const useStores = (): StoresType => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStores must be used within a StoreProvider');
  }
  return context;
};

export const useProfileStore = () => {
  const stores = useStores();
  return stores.useProfileStore;
};

export const useUserStore = () => {
  const stores = useStores();
  return stores.useUserStore;
};

export const useMilestonesStore = () => {
  const stores = useStores();
  return stores.useMilestonesStore;
};

export const useConcernsStore = () => {
  const stores = useStores();
  return stores.useConcernsStore;
};

export const useWellVisitsStore = () => {
  const stores = useStores();
  return stores.useWellVisitsStore;
};

export const useActionsStore = () => {
  const stores = useStores();
  return stores.useActionsStore;
};

export const useContentStore = () => {
  const stores = useStores();
  return stores.useContentStore;
};